import React, { useEffect, useMemo, useState } from 'react';
import Helmet from 'react-helmet';
import { Edit as EditIcon } from '@mui/icons-material';
import { Grid, Tooltip } from '@mui/material';
import { DataGridPro, GridActionsCellItem, GridColDef } from '@mui/x-data-grid-pro';
import { useHistory } from 'react-router-dom';
import { DataFilter, Loadable, TableLevelAction } from '../../../common';
import { Portlet, PortletBody, PortletHeader } from '../../../partials/content/Portlet';
import makeStyles from '@mui/styles/makeStyles';
import { dataGridStyles } from '../../../common/XDataGrid/common';
import { PortletHeaderToolbar } from '../../../partials/content/Portlet';


const useStyles = makeStyles(() => ({
    search: {
        width: 400,
    },
}));

export default function FacilityBrandingPage({
    facilityBrandingList,
    getFacilityBrandingListPending,
    actions: { getFacilityBrandingList },
}) {
    const history = useHistory();
    const [searchText, setSearchText] = useState('');
    const classes = useStyles();

    useEffect(() => {
        getFacilityBrandingList();
    }, [getFacilityBrandingList]);

    function handleRefresh() {
        getFacilityBrandingList();
    }

    function handleEdit(facilityId) {
        history.push(`/configuration-branding/branding/edit/${facilityId}`);
    }

    const filteredRows = useMemo(() => {
        const q = (searchText || '').trim().toLowerCase();

        const base = (facilityBrandingList || []).map((r) => ({
            ...r,

            // Prompt Pay formatting
            promptPayStatus: r.isPromptPayEnabled ? 'Enabled' : 'Disabled',
            promptPayPercentageDisplay:
                r.promptPayPercentage != null ? `${r.promptPayPercentage}%` : '-',
            promptPayDaysDisplay:
                r.promptPayDays != null ? r.promptPayDays : '-',

            // Self Pay formatting
            selfPayStatus: r.isSelfPayEnabled ? 'Enabled' : 'Disabled',
            selfPayPercentageDisplay:
                r.selfPayPercentage != null ? `${r.selfPayPercentage}%` : '-',
            selfPayContractName:
                r.selfPayContractName != null ? r.selfPayContractName : '-'

        }));

        if (!q) return base;

        return base.filter((r) =>
            [
                r.facilityName,
                r.promptPayStatus,
                r.promptPayPercentageDisplay,
                r.promptPayDaysDisplay,
                r.selfPayStatus,
                r.selfPayPercentageDisplay,
                r.selfPayContractName
            ]
                .join(' ')
                .toLowerCase()
                .includes(q)
        );
    }, [facilityBrandingList, searchText]);

    const columns: GridColDef[] = useMemo(() => [
        {
            field: 'facilityName',
            headerName: 'Facility Name',
            flex: 2,
            sortable: true,
            filterable: true,
        },
        {
            field: 'promptPayStatus',
            headerName: 'Prompt Pay',
            flex: 1,
        },
        {
            field: 'promptPayPercentageDisplay',
            headerName: 'Prompt Pay %',
            flex: 1,
        },
        {
            field: 'promptPayDays',
            headerName: 'Prompt Pay Days',
            flex: 1,
            renderCell: ({ value }) => value != null ? value : '-',
        },
        {
            field: 'selfPayStatus',
            headerName: 'Self Pay',
            flex: 1,
        },
        {
            field: 'selfPayPercentageDisplay',
            headerName: 'Self Pay %',
            flex: 1,
        },
        {
            field: 'selfPayContractName',
            headerName: 'Self Pay Contract',
            flex: 1,
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            flex: 0.5,
            getActions: ({ row }) => [
                <GridActionsCellItem
                color='primary'
                    icon={
                        <Tooltip title="Edit">
                            <EditIcon />
                        </Tooltip>
                    }
                    label="Edit"
                    onClick={() => handleEdit(row.facilityId)}
                />
            ],
        },
    ], []);

    return (
        <>
            <Helmet title="Branding" />
            <Portlet>
                <PortletHeader
                    title="Branding"
                    toolbar={
                        <PortletHeaderToolbar>
                            <DataFilter
                                onChange={setSearchText}
                                placeholder="Search"
                                cssClass={classes.search}
                            />
                        </PortletHeaderToolbar>
                    }
                />
                <PortletBody>
                    <TableLevelAction
                        handleRefresh={handleRefresh}
                        showFilterColumnsDialog={false}
                        actionLevelGrid={() => (
                            <Grid container>
                                <Grid item md={12}>
                                    <h6>{`Total Records : ${filteredRows.length}`}</h6>
                                </Grid>
                            </Grid>
                        )}
                    />
                    <Loadable loading={getFacilityBrandingListPending}>
                        <DataGridPro
                            rows={filteredRows}
                            columns={columns}
                            getRowId={(row) => row.facilityId}
                            disableRowSelectionOnClick
                            disableColumnFilter
                            pagination
                            density="compact"
                            initialState={{
                                pagination: { paginationModel: { pageSize: 10 } },
                            }}
                            pageSizeOptions={[10, 25, 50, 100]}
                            sortingMode="client"
                            filterMode="client"
                            paginationMode="client"
                            autoHeight
                            sx={{
                                '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                                    outline: 'none',
                                },
                                ...dataGridStyles,
                            }}
                        />
                    </Loadable>
                </PortletBody>
            </Portlet>
        </>
    );
}